import React, { useState, useEffect, memo } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';
import { toLocalDateStr } from '../../../utils/dateUtils';

/**
 * NextPrestation - Widget "Ma prochaine prestation"
 *
 * Affiche la prochaine prestation de l'employé avec détails.
 */
const NextPrestation = memo(({ token, userId, onNavigate }) => {
  const [prestation, setPrestation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNextPrestation = async () => {
      if (!token || !userId) return;

      setLoading(true);
      try {
        const today = toLocalDateStr(new Date());
        const futureDate = new Date();
        futureDate.setMonth(futureDate.getMonth() + 3);
        const endDate = toLocalDateStr(futureDate);

        const response = await fetch(
          `${API_BASE_URL}/api/prestations/my-planning?start_date=${today}&end_date=${endDate}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();

        if (data.success && data.data && data.data.length > 0) {
          // Trier par date de début et prendre la première
          const sorted = data.data.sort((a, b) =>
            new Date(a.start_date) - new Date(b.start_date)
          );
          setPrestation(sorted[0]);
        } else {
          setPrestation(null);
        }
      } catch (err) {
        console.error('Erreur fetch next prestation:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNextPrestation();
  }, [token, userId]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('fr-FR', options);
  };

  const formatDateRange = (start, end) => {
    if (start === end) {
      return formatDate(start);
    }
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h4 style={styles.title}>Ma prochaine prestation</h4>
        </div>
        <div style={styles.loadingContent}>
          <div style={styles.spinner}></div>
          <span style={styles.loadingText}>Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h4 style={styles.title}>Ma prochaine prestation</h4>
        </div>
        <div style={styles.errorContent}>
          <span style={styles.errorIcon}>!</span>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!prestation) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h4 style={styles.title}>Ma prochaine prestation</h4>
        </div>
        <div style={styles.emptyContent}>
          <span style={styles.emptyIcon}>📅</span>
          <p style={styles.emptyText}>Aucune prestation planifiée</p>
          <span style={styles.emptySubtext}>Votre planning est libre pour les prochaines semaines</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Ma prochaine prestation</h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('planning')}
        >
          Voir tout
        </button>
      </div>

      <div style={styles.prestationCard}>
        {/* Badge type */}
        <div style={styles.badgeRow}>
          <span style={{
            ...styles.typeBadge,
            backgroundColor: prestation.type === 'blocked' ? '#ffc107' : '#28a745'
          }}>
            {prestation.type === 'blocked' ? 'B' : 'P'}
          </span>
          <span style={styles.prestationNumber}>
            {prestation.prestation_number}
          </span>
          {prestation.client_name && (
            <span style={styles.clientName}>• {prestation.client_name}</span>
          )}
        </div>

        {/* Lieu et distance */}
        {(prestation.location_name || prestation.distance_km) && (
          <div style={styles.infoRow}>
            <span style={styles.icon}>📍</span>
            <span style={styles.infoText}>
              {prestation.location_name || 'Lieu non défini'}
              {prestation.distance_km && (
                <span style={styles.distance}> • {prestation.distance_km} km</span>
              )}
            </span>
          </div>
        )}

        {/* Dates */}
        <div style={styles.infoRow}>
          <span style={styles.icon}>📅</span>
          <span style={styles.infoText}>
            {formatDateRange(prestation.start_date, prestation.end_date)}
          </span>
          {prestation.departure_time && (
            <span style={styles.departureTime}>
              Départ {prestation.departure_time}
            </span>
          )}
        </div>

        {/* Chef d'équipe et participants */}
        <div style={styles.infoRow}>
          <span style={styles.icon}>👤</span>
          <span style={styles.infoText}>
            Chef : {prestation.team_leader_name || 'Non assigné'}
          </span>
          {prestation.participant_count !== undefined && (
            <span style={styles.participantCount}>
              {prestation.participant_count} participant{prestation.participant_count > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Bouton voir détail */}
        <button
          style={styles.detailButton}
          onClick={() => onNavigate && onNavigate('planning')}
        >
          Voir détail
        </button>
      </div>
    </div>
  );
});

NextPrestation.displayName = 'NextPrestation';

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  loadingContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '40px 20px',
    color: '#6c757d'
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid #e9ecef',
    borderTop: '2px solid #6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    fontSize: '0.95rem'
  },
  errorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545'
  },
  errorIcon: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#dc3545',
    color: 'white',
    borderRadius: '50%',
    fontWeight: 'bold',
    fontSize: '0.8rem'
  },
  emptyContent: {
    textAlign: 'center',
    padding: '30px 20px'
  },
  emptyIcon: {
    fontSize: '2.5rem',
    display: 'block',
    marginBottom: '12px'
  },
  emptyText: {
    margin: '0 0 8px 0',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#1a1a2e'
  },
  emptySubtext: {
    color: '#6c757d',
    fontSize: '0.9rem'
  },
  prestationCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '10px',
    padding: '16px',
    border: '1px solid #e9ecef'
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px'
  },
  typeBadge: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.9rem'
  },
  prestationNumber: {
    fontWeight: '600',
    fontSize: '1rem',
    color: '#1a1a2e'
  },
  clientName: {
    color: '#6c757d',
    fontSize: '0.95rem'
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    flexWrap: 'wrap'
  },
  icon: {
    fontSize: '1rem',
    width: '20px',
    textAlign: 'center'
  },
  infoText: {
    fontSize: '0.95rem',
    color: '#1a1a2e'
  },
  distance: {
    color: '#6c757d'
  },
  departureTime: {
    marginLeft: 'auto',
    backgroundColor: '#e9ecef',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.85rem',
    color: '#495057'
  },
  participantCount: {
    marginLeft: 'auto',
    color: '#6c757d',
    fontSize: '0.9rem'
  },
  detailButton: {
    marginTop: '12px',
    width: '100%',
    padding: '10px',
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  }
};

export default NextPrestation;
